import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_QUICKBOOKS_CUSTOMERS, GET_QUICKBOOKS_CONNECTION_STATUS } from '../../graphql/quickbooks';

/**
 * Customer filter component for Work Order Reports
 * Allows filtering work orders by QuickBooks customers (property owners/clients)
 */
function WorkOrderCustomerFilter({
  selectedCustomers,
  onChange,
  className = ''
}) {
  // Check QuickBooks connection status
  const { data: connectionData, loading: connectionLoading } = useQuery(
    GET_QUICKBOOKS_CONNECTION_STATUS,
    { fetchPolicy: 'cache-first' }
  );

  // Fetch QuickBooks customers
  const { data: customersData, loading: customersLoading } = useQuery(
    GET_QUICKBOOKS_CUSTOMERS,
    {
      skip: !connectionData?.quickBooksConnectionStatus?.isConnected,
      fetchPolicy: 'network-only',
    }
  );

  const isConnected = connectionData?.quickBooksConnectionStatus?.isConnected;
  const isLoading = connectionLoading || customersLoading;

  // Sort and filter active customers
  const customers = useMemo(() => {
    if (!customersData?.quickBooksCustomers) return [];
    
    return [...customersData.quickBooksCustomers]
      .filter(customer => customer.active)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [customersData]);

  const handleCustomerToggle = (customerId) => {
    const isSelected = selectedCustomers.includes(customerId);
    
    if (isSelected) {
      onChange(selectedCustomers.filter(id => id !== customerId));
    } else {
      onChange([...selectedCustomers, customerId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === customers.length) {
      onChange([]);
    } else {
      onChange(customers.map(c => c.id));
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  // Get customer name by ID (for display purposes)
  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return customerId;
    return customer.companyName 
      ? `${customer.displayName} (${customer.companyName})`
      : customer.displayName;
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        <span className="flex items-center">
          <svg className="w-4 h-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Customer/Owner Filter
        </span>
      </label>

      {/* Not Connected State */}
      {!isLoading && !isConnected && (
        <div className="rounded-md bg-yellow-50 p-3 border border-yellow-200">
          <div className="flex">
            <svg className="h-5 w-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="ml-2 text-sm text-yellow-700">
              QuickBooks is not connected. Connect in Settings to filter by customer.
            </p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center text-sm text-gray-500 py-2">
          <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading customers...
        </div>
      )}

      {/* Connected State with Customers */}
      {!isLoading && isConnected && (
        <div className="space-y-2">
          {/* Dropdown/Multi-select */}
          <div className="relative">
            <details className="group">
              <summary className="flex items-center justify-between w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                <span className="text-gray-700">
                  {selectedCustomers.length === 0 
                    ? 'All customers (no filter)' 
                    : `${selectedCustomers.length} customer${selectedCustomers.length !== 1 ? 's' : ''} selected`
                  }
                </span>
                <svg className="w-4 h-4 text-gray-500 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              
              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {/* Quick actions */}
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                  >
                    {selectedCustomers.length === customers.length ? 'Deselect All' : 'Select All'}
                  </button>
                  {selectedCustomers.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearAll}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Customer list */}
                {customers.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-gray-500 text-center">
                    No customers found in QuickBooks
                  </div>
                ) : (
                  <div className="py-1">
                    {customers.map((customer) => (
                      <label
                        key={customer.id}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCustomers.includes(customer.id)}
                          onChange={() => handleCustomerToggle(customer.id)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {customer.displayName}
                          {customer.companyName && (
                            <span className="text-gray-500 ml-1">({customer.companyName})</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </details>
          </div>

          {/* Selected customers tags */}
          {selectedCustomers.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {selectedCustomers.slice(0, 3).map((customerId) => (
                <span
                  key={customerId}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                >
                  {getCustomerName(customerId).length > 25 
                    ? getCustomerName(customerId).substring(0, 25) + '...'
                    : getCustomerName(customerId)
                  }
                  <button
                    type="button"
                    onClick={() => handleCustomerToggle(customerId)}
                    className="ml-1 text-purple-600 hover:text-purple-800"
                    aria-label={`Remove ${getCustomerName(customerId)}`}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </span>
              ))}
              {selectedCustomers.length > 3 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  +{selectedCustomers.length - 3} more
                </span>
              )}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-1">
            Filter work orders by property owner/client
          </p>
        </div>
      )}
    </div>
  );
}

export default WorkOrderCustomerFilter;


